// Travel partner is a DMO or website that embeds our chat widget.

import { Timestamp } from "@/types/date"
import { EmbeddedProps } from "@/types/embedded"
import { PlaceRef } from "@/types/entity"
import { InspirationPublishMode } from "@/types/inspiration"
import { LocationSearchHit, PlaceSummary } from "@/types/place"
import isEmpty from "lodash/isEmpty"

import { EventOrganizationProperties } from "@/lib/analytics/amplitude-types"
import { now } from "@/lib/date-utils"

export const ORGANIZATION_STATUSES = [
  "demo",
  "pending",
  "live",
  "inactive",
] as const
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number]

export const BUSINESS_TYPES = ["destination", "hotel"] as const
export type BusinessType = (typeof BUSINESS_TYPES)[number]

export const GEO_TYPES = [
  "international",
  "state",
  "major_city",
  "small_city",
  "regional",
  "hospitality",
  "media",
] as const
export type GeoType = (typeof GEO_TYPES)[number]

export const GEO_TYPE_LABELS: Record<GeoType, string> = {
  international: "International / National DMO",
  state: "State / Province DMO",
  major_city: "Major City DMO",
  small_city: "Small City / Town DMO",
  regional: "Regional / County DMO",
  hospitality: "Hospitality Property",
  media: "Media / Other",
}

export type FrameSize = "lg" | "xl"
export type ButtonPosition = "right" | "bottom-right"

export type OnboardStepId =
  | "load-content"
  | "tailor-look"
  | "create-guides"
  | "select-ctas"
  | "designate-partners"
  | "review-responses"
  | "install-snippet"
  | "add-team-members"
  | "go-live"

export type OnboardStepStatus = "completed" | "in_progress" | "not_started"

export type OnboardStatus = Partial<Record<OnboardStepId, OnboardStepStatus>>

export type OrganizationProperties = Partial<{
  button_label: string
  button_color: string
  accent_color: string
  button_position: string
  brand_logo: string
  hide_logo_in_pdf: boolean
  hide_place_photos_in_pdf: boolean
  prompt_header: string
  start_location: string
  boundary_location: string
  default_map_view: string
  bot_welcome_prompt: string
  frame_size: FrameSize
  content_search_enabled: boolean
  unverified_message_limit: number
  book_direct_only: boolean
  flight_checkout_enabled: boolean
  hide_short_term_rentals: boolean
  hide_related_stays: boolean
  show_review_highlights: boolean
  show_attractions_outside_geo: boolean
  featured_hotels_only: boolean
  microsite_host?: string | null
  answer_intelligence_status: "preview" | "live" | "" | null
  event_web_search_enabled: boolean
  trail_web_search_enabled: boolean
  example_user_messages: string[]
  chat_placeholder: string
  disclaimer_line: string
  what_can_i_ask_button_text: string
  alternate_website_urls: string[]
  content_website_urls: string[]
  gtm_container_id: string
  partner_analytics_enabled: boolean
  content_crawl_paths: string[]
  content_exclude_paths: string[]
  hide_inspirations: boolean
  show_explore_page: boolean
  hide_create_trip: boolean
  whatsapp_phone_number: string
  whatsapp_status: "pending" | "live" | "" | null
  whatsapp_activated_at: string | null
  whatsapp_default_user_message: string
  translation_locales: string
  show_newsletter_subscription: boolean
  newsletter_subscription_label: string
  newsletter_subscription_default_checked: boolean
  hide_label_on_mobile: boolean
  inspiration_handles: string[]
  customer_copies_enabled: boolean
  default_publish_mode: InspirationPublishMode
  dashboard_ids: string[]
  hide_external_web_links: boolean
  geo_type: GeoType | null
  default_region: string
  map_viewport: { lat: number; lng: number; zoom: number } | null
  brand_voice: string
  enabled_features: string[]
  // derived properties
  has_crawled_articles: boolean
  has_featured_places: boolean
  has_report_exports: boolean
  has_listing_imports: boolean
  ignore_external_web_links: boolean
  allowed_domains: string[]
  partner_booking_url: string
  key_fact_summary: string
}>

export type Organization = {
  id?: number | null
  name: string
  orgid: string
  status: OrganizationStatus
  business_type: BusinessType
  businesses?: Array<{
    name: string
    entity_ref: string
    type: string
    city: string
  }>
  creator?: {
    handle: string
    email: string
  } | null
  // origin hostname with scheme e.g. https://visitcalifornia.com
  website_url: string
  geo_name?: string
  properties: OrganizationProperties
  created_at: Timestamp
  activated_at?: Timestamp
  locations?: OrgLocation[]
}

export type OrgFeatures = {
  ais: boolean
  flights: boolean
  brand_voice: boolean
  whatsapp: boolean
  languages: boolean
}

export type OrganizationList = {
  id: number
  name: string
  location?: string
  orgid: string
  status: OrganizationStatus
  website_url: string
  business_type: BusinessType
  latlong: string | null
  features: OrgFeatures
}[]

// additional transient properties set at runtime
export type OrganizationContext = Organization & {
  // full path on partner site without scheme, e.g. visitcalifornia.com/monterey
  source_url: string
}

export type OrgLocation = LocationSearchHit

// geos carved out of an org's coverage (excluded_geo_ids)
export type ExcludedLocation = {
  name: string
  entity_ref: PlaceRef
  ancestors: string[]
}

// the org site's visual identity read off its rendered homepage: colors as
// computed CSS strings (e.g. "rgb(12, 34, 56)"). All fields nullable.
export type SitePalette = {
  background: string | null
  text: string | null
  heading: string | null
  link: string | null
  heading_font: string | null
  body_font: string | null
  // "serif" | "sans-serif" — the fallback while the real face loads (or never does)
  heading_font_generic: string | null
  body_font_generic?: string | null
  // heading treatment beyond color
  heading_weight?: string | null
  heading_spacing?: string | null
  heading_transform?: string | null
  // the brand's primary CTA styling
  button_background?: string | null
  button_color?: string | null
  button_radius?: string | null
  button_transform?: string | null
  // the Google Fonts stylesheet the page itself loads, if any
  font_css_url?: string | null
  theme_color: string | null
  logo_url: string | null
  hero_image: string | null
}

// additional fields for admin dashboard
export type OrganizationAdmin = Organization & {
  time_zone: string
  lock_version: number
  location_url?: string
  locations: OrgLocation[]
  onboard_status?: OnboardStatus
  secure_properties?: Record<string, string>
  country_name?: string
  excluded_locations?: ExcludedLocation[]
  ais_similar_locations?: OrgLocation[]
  site_palette?: SitePalette | null
}

export type OrgMicrosite = {
  hostname?: string
  username?: string
  password?: string | null
}

export type OrgAuditLog = {
  id: number
  admin: string
  timestamp: Timestamp
  action: string
  changes: object[]
  user: string
  agent_session_id?: number
}

export type CrawledUrlSource = "web" | "file"

export type OrgCrawledUrl = {
  id: number
  url: string
  mentions: number
  clicks: number
  closed: number
  crawled_at: Timestamp
  deactivated_at?: Timestamp
  s3_url?: string
  expires_at?: Timestamp
}

export type ClosedPlace = {
  id: string
  name: string
  type: string
  location: string
  canonical_url: string
  excerpt: string
  page_url: string | null
}

// counts keyed by job state, place status, or changed-field list
type Tally = Record<string, number>

export type PlacesStatusPlace = {
  id: number
  name: string
  type: string
  geo: string | null
  canonical_url: string
}

export type PlacesStatusClosedPlace = PlacesStatusPlace & {
  status: "CLOSED" | "TEMPORARILY_CLOSED"
}

// PlaceRevamper.summarize_org_refresh: refresh_place and google_place_load are
// present only once every job spawned by the geo jobs has finished
export type PlacesStatusReport = {
  geo_jobs: number
  started_at?: Timestamp
  in_flight?: Tally
  refresh_place?: {
    jobs: Tally
    updated_fields: Tally
    status_changed_to: Tally
    newly_closed: PlacesStatusClosedPlace[]
  }
  google_place_load?: {
    jobs: Tally
    results: Partial<
      Record<
        "processed" | "found" | "skipped" | "not_found" | "over_limit",
        number
      >
    >
    places: number
    now_with_google_id: number
    matched_status: Tally
    // still TripAdvisor-only, so their status can't be checked
    unmatched: PlacesStatusPlace[]
  }
}

export type ArticleChunk = {
  id: string
  text: string
  source_url: string
  source_title: string
  date: number
}

export interface ImportedListing {
  id: string
  name: string
  category: string
  city: string
  state: string
  region: string
  status?: string
  feature_level: number
  invalid_fields: string[]
}

export interface OrgPlacePreview {
  feature_level: number
  overridden_at: string | null
  deactivated_at: string | null
}

export interface PlaceResolveResult {
  place: PlaceSummary | null
  org_place: OrgPlacePreview | null
}

export interface ImportedListingPreview {
  listing: ImportedListing
  preview_data?: PlaceSummary | null
  preview_org_place?: OrgPlacePreview | null
  preview_attempted?: boolean
  is_previewing?: boolean
}

export interface OrganizationAction {
  id: number
  action_id: string
  button_label: string
  user_prompt?: string
  message_hint?: string
  prompt_editable?: boolean
  guide_path?: string
  place_path?: string
  media_link?: string
  clicks?: number
  created_at: Timestamp
  updated_at: Timestamp
}

// Top of the org chat "message count" filter range; the max bucket means "N+".
export const MESSAGE_COUNT_FILTER_MAX = 10

export const CHAT_USER_INTENT = {
  ALL: "ALL",
  ACTIVITIES: "ACTIVITIES",
  BUDGET: "BUDGET",
  DINING: "DINING",
  FLIGHTS: "FLIGHTS",
  INTEREST_BASED: "INTEREST_BASED",
  ITINERARY: "ITINERARY",
  LODGING: "LODGING",
  OTHER: "OTHER",
  RESERVATIONS: "RESERVATIONS",
  TIRE_KICKING: "TIRE_KICKING",
  TRANSPORTATION: "TRANSPORTATION",
  UNCLEAR_INTENT: "UNCLEAR_INTENT",
} as const

export type ChatUserIntent =
  (typeof CHAT_USER_INTENT)[keyof typeof CHAT_USER_INTENT]

export type FeedbackType = "thumbs_up" | "thumbs_down" | undefined

// --- Place suggestions tuning sandbox ---

export type PlaceSuggestionKnobType =
  | "int"
  | "float"
  | "bool"
  | "enum"
  | "enum_multi"

// UI prominence for a knob: `headline` facets are the marquee best-of curation
// dials; `advanced` ones sit behind progressive disclosure.
export type PlaceSuggestionKnobTier = "headline" | "advanced"

// A single tweakable ranking facet returned by the /knobs endpoint.
export interface PlaceSuggestionKnob {
  key: string
  // Human-facing name (falls back to a humanized key on the backend).
  label: string
  tier: PlaceSuggestionKnobTier
  type: PlaceSuggestionKnobType
  values: string[] | null
  default: unknown
  doc: string
}

// The org's best-of curation: Mindtrip's default seed bundle plus the facets
// this org has explicitly overridden (parsed from its saved policy string).
// Keyed by facet key; the editor prefills each headline knob from `saved` over
// `default`.
export interface PlaceSuggestionBestOf {
  default: Record<string, unknown>
  saved: Record<string, unknown>
}

// An org-level DMO policy knob returned by the /knobs endpoint. Distinct from
// the per-query PlaceSuggestionKnob facets: these are the saved org levers
// (boosts, fairness damping, geo interpretation).
export type PlaceSuggestionDmoKnobType =
  | "string"
  | "boolean"
  | "integer"
  | "float"

export interface PlaceSuggestionDmoKnob {
  key: string
  type: PlaceSuggestionDmoKnobType
  default: unknown
  // This org's current saved value — prefill controls from this.
  value: unknown
  // Present only for default_geo_search_type (render a dropdown).
  values: string[] | null
  doc: string
}

export type PlaceSuggestionPlaceType = "hotel" | "restaurant" | "attraction"

// Decomposition of a result's `_score` into featured/paid vs organic points.
// Invariant: featured.total + organic === score.
export interface PlaceSuggestionScoreBreakdown {
  featured: {
    gross: number // raw featured tier boost (e.g. 250 for L2, 99 for L1)
    penalty: number // fairness-damping points subtracted (>= 0)
    net: number // gross - penalty, floored at 0
    partner: number // flat partner boost (e.g. +8)
    total: number // net + partner — total "paid/featured" points
  }
  organic: number // everything else (reviews/rating/guide/michelin/hip)
}

export interface PlaceSuggestionResult {
  rank: number
  score: number
  entity_id: string
  entity_ref: string
  name: string
  photo_url: string | null
  // 1 = single-boosted partner (L1), 2 = double-boosted (L2), null = organic
  feature_level: 1 | 2 | null
  recommendation_count: number
  // Nightly $ band low/high and where this hotel's price sits in the market
  // (0–100). Null when unknown.
  estimated_price_low: number | null
  estimated_price_high: number | null
  price_percentile: number | null
  // Score decomposition (featured/paid vs organic). Null for non-org results.
  score_breakdown: PlaceSuggestionScoreBreakdown | null
}

// Market price context for the searched location.
export interface PlaceSuggestionMarket {
  median: number
  tiers: Record<string, unknown>
  percentiles: Record<string, unknown>
}

// One serialized SearchReport entry ({key, text, value}) — the shape of every
// row in the report's sections (arguments, search_plan, notes, …).
export interface PlaceSuggestionProvenanceEntry {
  key: string
  text: string
  value: unknown
}

// The re-runnable effective overrides, as {key, value} rows (from the backend's
// override_rows) — every overridable signal that resolved to a value.
export interface PlaceSuggestionOverrideRow {
  key: string
  value: unknown
}

// report.to_h — the report's sectioned entries — plus the derived `overrides`
// rows the debug drawer and re-run string read from.
export interface PlaceSuggestionProvenance {
  overrides: PlaceSuggestionOverrideRow[]
  arguments?: PlaceSuggestionProvenanceEntry[]
  search_plan?: PlaceSuggestionProvenanceEntry[]
  notes?: PlaceSuggestionProvenanceEntry[]
  results?: PlaceSuggestionProvenanceEntry[]
  recovery?: PlaceSuggestionProvenanceEntry[]
  adjustments?: PlaceSuggestionProvenanceEntry[]
  your_turn?: PlaceSuggestionProvenanceEntry[]
}

export interface PlaceSuggestionPreview {
  results: PlaceSuggestionResult[]
  provenance: PlaceSuggestionProvenance
  search_string: string
  location: string
  location_entity_ref?: string | null
  place_type?: string
  expected?: string | null
  preview_limit: number
  presentation_limit: number
  // Market price context for the searched location (null when unavailable).
  market: PlaceSuggestionMarket | null
  // The engine's leave-one-out relaxation probe text ("without <gate> → N
  // matches"), present only when the query returned zero results.
  zero_result_diagnostic?: string | null
}

// One of the geos this DMO has turned on — the location picker's options. The
// entity_ref (an opaque Location ref) is what preview/diagnose/evaluate resolve.
export interface PlaceSuggestionGeo {
  name: string
  entity_ref: string
  ancestors?: string[]
}

// A saved benchmark query — part of this DMO's search eval. `expected` is an
// optional human note of what the DMO wants this query to surface; the doctor
// treats it as ground truth for that query.
export interface PlaceSuggestionBenchmark {
  search_string: string
  location_entity_ref: string | null
  note?: string | null
  expected?: string | null
}

// The shared request body for a sandbox run — used by both preview and the
// single-query diagnose. Location is sent as an opaque Location entity_ref
// (one of the org's turned-on geos), not a free string.
export interface PlaceSuggestionRunBody {
  search_string: string
  location_entity_ref: string | null
  place_type: string
  manual_overrides: string
  preview_limit: number
  presentation_limit: number
  property_overrides: Record<string, unknown>
}

export interface PlaceSuggestionKnobsResponse {
  knobs: PlaceSuggestionKnob[]
  dmo_knobs: PlaceSuggestionDmoKnob[]
  best_of: PlaceSuggestionBestOf
  geos: PlaceSuggestionGeo[]
  default_presentation_limit: number
  max_preview_limit: number
  notes: string | null
  benchmarks: PlaceSuggestionBenchmark[]
}

// --- Search doctor (LLM diagnosis) ---

export type PlaceSuggestionVerdict = "good" | "mixed" | "poor"
export type PlaceSuggestionSeverity = "info" | "warn" | "critical"
export type PlaceSuggestionWishMet = "yes" | "partial" | "no"
// Where an applyable suggestion lands: org ranking property, saved best-of
// curation, or a per-run override.
export type PlaceSuggestionKnobScope = "org_policy" | "best_of" | "per_query"

export interface PlaceSuggestionResultFlag {
  rank: number
  entity_ref: string | null
  severity: PlaceSuggestionSeverity
  issue: string
}

export interface PlaceSuggestionKnobSuggestion {
  scope: PlaceSuggestionKnobScope
  key: string
  current: unknown
  suggested: unknown
  rationale: string
}

export interface PlaceSuggestionDiagnosis {
  overall: {
    score: number
    verdict: PlaceSuggestionVerdict
    headline: string
    summary: string
  }
  wish_alignment: {
    wish: string
    met: PlaceSuggestionWishMet
    evidence: string
  }[]
  per_query: {
    search_string: string
    score: number
    assessment: string
    result_flags: PlaceSuggestionResultFlag[]
  }[]
  data_quality_flags: { issue: string; examples: string[] }[]
  knob_suggestions: PlaceSuggestionKnobSuggestion[]
}

export function getOrgid(query: URLSearchParams): string | undefined {
  return query.get("orgid")?.replace(/^<|>$/g, "")
}

export function getOrganizationUrl(orgUrl: string | undefined | null): string {
  return "https://" + orgUrl?.split("/")[0]
}

function getCtaType(props?: EmbeddedProps): string | undefined {
  if (!props) return undefined

  // check both link (button) and route (inline link) for url patterns
  const linkOrRoute = props.link || props.route

  // these match patterns in initEmbeddedChat
  const guideLink = linkOrRoute?.match(
    /\b(explore|inspiration)\/.*\/(in-[^/?]+)\b/
  )
  const placeLink = linkOrRoute?.match(
    /\b(activity|attraction|event|hotel|location|restaurant)\/.*\/((ac|at|ev|re|ho|lo)-[^/?]+)\b/
  )

  if (!isEmpty(props.vibes)) return "create-trip"
  if (props.route && /new\/trip/.test(props.route)) return "open-trip-modal"
  if (props.prompt && guideLink) return "open-chat-from-guide"
  if (props.prompt && placeLink) return "open-chat-from-place"
  if (guideLink) return "view-guide"
  if (placeLink) return "view-place"
  if (props.prompt && props.link) return "open-chat-media-link"
  if (props.prompt && props.intro) return "open-chat-user-prompt"
  if (props.prompt) return "open-chat-sample-prompt"
  if (!props.prompt) return "open-chat"

  return undefined
}

export function getAnalyticsProperties(
  org: OrganizationContext,
  props?: EmbeddedProps
): EventOrganizationProperties["properties"] {
  return {
    org_name: org.name,
    geo_name: org.geo_name,
    source_url: org.source_url,
    action_cue: props?.button,
    message_cue: props?.intro,
    user_message: props?.prompt,
    media_url: props?.link,
    cta_id: props?.id,
    cta_type: getCtaType(props),
    open_trigger: props?.open_trigger,
    host_referrer: props?.host_referrer,
    click_ids: props?.click_ids,
  }
}

export function makeOrganization(
  org: Organization | null,
  props: {
    orgid?: string
    orgUrl?: string
    geoName?: string
  }
): OrganizationContext {
  if (!org) {
    org = {
      id: null,
      name: "unknown",
      orgid: props.orgid || "unknown",
      website_url: "",
      status: "demo",
      business_type: "destination",
      properties: {},
      created_at: now(),
    }
  }

  return {
    ...org,
    source_url: props.orgUrl || "unknown",
    website_url: org.website_url || getOrganizationUrl(props.orgUrl),
    geo_name: props.geoName || org.geo_name,
  }
}
